import {InfoButton, Observer, RunInAction_Set, VDateTime} from "web-vcore";
import {StartDownload} from "js-vextensions";
import moment from "moment";
import {Button, CheckBox, Column, DropDown, DropDownContent, DropDownTrigger, Row, Spinner, Text} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {Session} from "../../../Server/DB/sessions/@Session.js";
import {store} from "../../../Store/index.js";
import {zIndexes} from "../../../Utils/UI/ZIndexes.js";
import React from "react";
import {observer_mgl} from "mobx-graphlink";
import {gdBelt_rawDataDB} from "../../../Utils/IndexedDB/GDBelt_RawDataDB.js";
import {Filesystem, Directory, Encoding} from "@capacitor/filesystem";
import {Android_VLog, VLogLevel} from "../../../Server/DB/@Shared/EffectPointerPlayer.js";

export const SessionsUISettingsDropdown = observer_mgl((props: {sessions: Session[]})=>{
	const {sessions} = props;
	const uiState = store.main.timeline.sessions;
	const {export_includeAll, export_rangeStart, export_rangeEnd} = uiState;

	const sessionsInRange = export_includeAll ? sessions : sessions.filter(session=>{
		const rangeStartMs = export_rangeStart ? moment(export_rangeStart, "YYYY-MM-DD").startOf("day").valueOf() : -Infinity;
		const rangeEndMs = export_rangeEnd ? moment(export_rangeEnd, "YYYY-MM-DD").endOf("day").valueOf() : Infinity;
		return session.startTime >= rangeStartMs && session.startTime <= rangeEndMs;
	});
	const exportSuffix = export_includeAll ? "all" : "in range";
	const exportDateRangeStr = export_includeAll
		? `AllAt${moment().format("YYYY-MM-DD-HH-mm-ss")}`
		: `${export_rangeStart ?? "Open"}_To_${export_rangeEnd ?? "Open"}`;
	return (
		<DropDown style={{marginLeft: 3}}>
			<DropDownTrigger>
				<Button mdIcon="cog" size={32}/>
			</DropDownTrigger>
			<DropDownContent style={{zIndex: zIndexes.dropDown, right: 0, width: 350, background: "rgba(0,0,0,.9)", borderRadius: 5}}><Column>
				<Row>Sessions, total: {sessions.length}{!export_includeAll && `, in range: ${sessionsInRange.length}`}</Row>
				<Row mt={5} center>
					<CheckBox text="Include all" value={export_includeAll} onChange={val=>RunInAction_Set(()=>uiState.export_includeAll = val)}/>
				</Row>
				{!export_includeAll && <>
					<Row mt={5} center>
						<Text style={{width: 85}}>Range start:</Text>
						<VDateTime dateFormat="YYYY-MM-DD" timeFormat={false}
							value={export_rangeStart ? moment(export_rangeStart, "YYYY-MM-DD") : null}
							onChange={val=>RunInAction_Set(()=>uiState.export_rangeStart = val?.format("YYYY-MM-DD"))}/>
					</Row>
					<Row mt={5} center>
						<Text style={{width: 85}}>Range end:</Text>
						<VDateTime dateFormat="YYYY-MM-DD" timeFormat={false}
							value={export_rangeEnd ? moment(export_rangeEnd, "YYYY-MM-DD") : null}
							onChange={val=>RunInAction_Set(()=>uiState.export_rangeEnd = val?.format("YYYY-MM-DD"))}/>
					</Row>
				</>}
				<Row mt={5}>
					<Button text={`Export sessions' core data (${exportSuffix})`} onClick={()=>{
						const output = JSON.stringify(sessionsInRange, null, "\t");
						StartDownload(output, `SessionsExport_${exportDateRangeStr}_Count${sessionsInRange.length}.json`);
					}}/>
				</Row>
				<Row mt={5}>
					<Button text={`Export sessions' breath-data (${exportSuffix})`} onClick={async()=>{
						const {output, count} = await GenerateBreathDataOutput(sessionsInRange);
						StartDownload(output, `BreathDataExport_${exportDateRangeStr}_Count${count}.json`);
					}}/>
				</Row>
			</Column></DropDownContent>
		</DropDown>
	);
});

export async function GenerateBreathDataOutput(sessions: Session[]) {
	const allBreathData = [] as {sessionId: string, chunks: any[]}[];
	for (const session of sessions) {
		const chunks = await gdBelt_rawDataDB.GetChunksForSession(session.id);
		if (chunks.length > 0) allBreathData.push({sessionId: session.id, chunks});
	}
	// use compact stringification for chunks arrays to avoid "Invalid string length" errors,
	// while keeping readable whitespace for the outer structure
	const entries = allBreathData.map(entry=>`
		{
			"sessionId": ${JSON.stringify(entry.sessionId)},
			"chunks": ${JSON.stringify(entry.chunks)}
		}
	`.AsMultiline(0));
	const output = `[\n${entries.join(",\n")}\n]`;
	return {output, count: allBreathData.length};
}

/** Streams session breath-data directly to a file, one session at a time, to avoid OOM when there are many sessions. */
export async function WriteBreathDataToFile(sessions: Session[], filePath: string, directory: Directory, onProgress?: (msg: string)=>void): Promise<{count: number, totalBytes: number}> {
	await Filesystem.writeFile({path: filePath, data: "[", directory, recursive: true, encoding: Encoding.UTF8});
	let totalBytes = 1; // for the opening "["
	let count = 0;
	const append = async(data: string)=>{
		await Filesystem.appendFile({path: filePath, data, directory, encoding: Encoding.UTF8});
		totalBytes += data.length;
	};

	const sessionsWithData = [] as Session[];
	for (const session of sessions) {
		if (await gdBelt_rawDataDB.GetFirstChunkForSession(session.id) != null) sessionsWithData.push(session);
	}

	for (const session of sessionsWithData) {
		onProgress?.(`Exporting session ${count + 1} / ${sessionsWithData.length}`);
		count++;
		const chunks = await gdBelt_rawDataDB.GetChunksForSession(session.id);
		if (chunks.length == 0) continue;

		await append(`${count == 0 ? "\n" : ",\n"}\t{\n\t\t"sessionId": ${JSON.stringify(session.id)},\n\t\t"chunks": [`);

		// OOM (often) happens if we stringify the chunks-array THEN concatenize that with/into other strings
		// so stringify then append-to-file immediately, as its own step (I think allowing faster memory reclaiming)
		await append(JSON.stringify(chunks));

		await append(`]\n\t}`);
	}

	await append("\n]");

	return {count, totalBytes};
}